/**
 * Unified Status Badge Component
 * Generic badge component for all status, health, and state indicators
 * Consolidates TaskHealthBadge, SubscriptionBadge, and similar implementations
 */

import { ReactNode } from 'react';
import { Badge } from './ui/badge';
import {
  WIDGET_STATUS_BADGE_DANGER_CLASS,
  WIDGET_STATUS_BADGE_INFO_CLASS,
  WIDGET_STATUS_BADGE_PURPLE_CLASS,
  WIDGET_STATUS_BADGE_SUCCESS_CLASS,
  WIDGET_STATUS_BADGE_WARNING_CLASS,
} from '../constants/widgetTheme';

export type BadgeTone = 
  | 'neutral'      // gray - for basic/normal states
  | 'info'         // blue - for informational states
  | 'success'      // green - for healthy/complete states
  | 'warning'      // yellow - for at-risk/needs attention
  | 'destructive'  // red - for errors/blocked/critical
  | 'premium';     // purple - for premium/enterprise states

export type BadgeSize = 'sm' | 'md' | 'lg';

interface StatusBadgeProps {
  /** Tone determines the semantic color */
  tone: BadgeTone;
  /** Icon to display (left of label) */
  icon?: ReactNode;
  /** Display text */
  label: string;
  /** Badge size */
  size?: BadgeSize;
  /** Optional class overrides */
  className?: string;
  /** Whether to use outline variant */
  variant?: 'default' | 'outline';
}

export default function StatusBadge({
  tone,
  icon,
  label,
  size = 'md',
  className = '',
  variant = 'default'
}: StatusBadgeProps) {
  const toneClasses: Record<BadgeTone, string> = {
    neutral: variant === 'outline' 
      ? 'border-border text-foreground bg-card' 
      : 'bg-muted text-muted-foreground border-border',
    
    info: `border ${WIDGET_STATUS_BADGE_INFO_CLASS}`,
    
    success: `border ${WIDGET_STATUS_BADGE_SUCCESS_CLASS}`,
    
    warning: `border ${WIDGET_STATUS_BADGE_WARNING_CLASS}`,
    
    destructive: `border ${WIDGET_STATUS_BADGE_DANGER_CLASS}`,
    
    premium: `border ${WIDGET_STATUS_BADGE_PURPLE_CLASS}`
  };

  const sizeClasses: Record<BadgeSize, string> = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  return (
    <Badge 
      variant={variant}
      className={`${toneClasses[tone]} ${sizeClasses[size]} font-medium flex items-center gap-1.5 w-fit ${className}`}
    >
      {icon && (
        <span className="flex-shrink-0">
          {icon}
        </span>
      )}
      <span>{label}</span>
    </Badge>
  );
}

/**
 * Helper to map task health status to badge tone + label
 */
export function getTaskHealthBadgeConfig(healthStatus: string): {
  tone: BadgeTone;
  label: string;
  icon?: ReactNode;
} {
  const configs: Record<string, { tone: BadgeTone; label: string; icon: ReactNode }> = {
    'overdue': { tone: 'destructive', label: 'Overdue', icon: null },
    'at-risk': { tone: 'warning', label: 'At Risk', icon: null },
    'needs-update': { tone: 'warning', label: 'Needs Update', icon: null },
    'blocked': { tone: 'destructive', label: 'Blocked', icon: null },
    'healthy': { tone: 'success', label: 'Healthy', icon: null }
  };
  return configs[healthStatus] || configs['healthy'];
}

/**
 * Helper to map subscription plan to badge tone + label + icon
 */
export function getSubscriptionBadgeConfig(plan: string): {
  tone: BadgeTone;
  label: string;
  icon?: ReactNode;
} {
  const configs: Record<string, { tone: BadgeTone; label: string; icon: ReactNode }> = {
    'Basic': { tone: 'neutral', label: 'Basic Plan', icon: null },
    'Plus': { tone: 'info', label: 'Plus Plan', icon: null },
    'Enterprise': { tone: 'premium', label: 'Enterprise Plan', icon: null }
  };
  return configs[plan] || configs['Basic'];
}
